import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterRenderEffect,
  computed,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import * as L from 'leaflet';

import { RedemptionLocation } from '../../../../services/company-group.service';
import { StorefrontService } from '../../../../services/storefront.service';

type MappedLocation = RedemptionLocation & { latitude: number; longitude: number };

const hasCoordinates = (loc: RedemptionLocation): loc is MappedLocation =>
  loc.latitude != null && loc.longitude != null;

/** Narrow screens stack the list under the map, so a list click has to bring the map into view. */
const STACKED_QUERY = '(max-width: 767px)';

/**
 * Map of the places where the storefront's vouchers can be redeemed, with the list of
 * those places beside it. List and map are linked both ways: choosing a place in the
 * list flies the map to it, choosing a pin highlights and reveals its list entry.
 * With showList off it is the plain map of a single shop.
 */
@Component({
  selector: 'app-redemption-map',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './redemption-map.component.html',
  styleUrl: './redemption-map.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RedemptionMapComponent {
  readonly locations = input.required<RedemptionLocation[]>();
  /** The list of places next to the map; off for a single shop. */
  readonly showList = input(true);

  private readonly storefrontService = inject(StorefrontService);
  private readonly mapEl = viewChild<ElementRef<HTMLDivElement>>('map');
  private readonly listEl = viewChild<ElementRef<HTMLUListElement>>('list');

  protected readonly mapped = computed(() => this.locations().filter(hasCoordinates));
  protected readonly activeId = signal<string | null>(null);

  private map: L.Map | undefined;
  private readonly markers = new Map<string, L.Marker>();

  constructor() {
    afterRenderEffect(() => {
      const el = this.mapEl()?.nativeElement;
      const mapped = this.mapped();
      untracked(() => this.renderMap(el, mapped));
    });
    // Leaflet owns the marker elements, so the active state is set on them directly.
    afterRenderEffect(() => {
      const activeId = this.activeId();
      for (const [id, marker] of this.markers) {
        marker.getElement()?.classList.toggle('is-active', id === activeId);
        marker.setZIndexOffset(id === activeId ? 1000 : 0);
      }
    });
    inject(DestroyRef).onDestroy(() => this.map?.remove());
  }

  protected countLabel(): string {
    const count = this.locations().length;
    return count === 1 ? 'in diesem Laden' : `in diesen ${count} Läden`;
  }

  protected address(loc: RedemptionLocation): string {
    const place = [loc.zip, loc.city].filter(Boolean).join(' ');
    return [loc.street, place].filter(Boolean).join(', ');
  }

  protected imageUrl(path: string): string {
    return this.storefrontService.getImageUrl(path);
  }

  protected websiteUrl(website: string): string {
    return /^https?:\/\//.test(website) ? website : `https://${website}`;
  }

  protected websiteLabel(website: string): string {
    return website.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  protected showOnMap(loc: RedemptionLocation): void {
    const marker = this.markers.get(loc.id);
    if (!marker || !this.map) return;
    this.map.flyTo(marker.getLatLng(), Math.max(this.map.getZoom(), 15), { duration: 0.6 });
    marker.openPopup();
    if (window.matchMedia(STACKED_QUERY).matches) {
      this.mapEl()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private renderMap(el: HTMLDivElement | undefined, mapped: MappedLocation[]): void {
    this.map?.remove();
    this.map = undefined;
    this.markers.clear();
    if (!el || mapped.length === 0) return;

    this.map = L.map(el, { scrollWheelZoom: false });
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      { attribution: '&copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ', maxZoom: 18 },
    ).addTo(this.map);

    for (const loc of mapped) {
      const marker = L.marker([loc.latitude, loc.longitude], {
        icon: this.markerIcon(loc),
        title: loc.company_name,
      })
        .addTo(this.map)
        .bindPopup(this.popupContent(loc));
      // popupopen/-close cover both ways in: a pin click and a list click.
      marker.on('popupopen', () => {
        this.activeId.set(loc.id);
        this.revealListItem(loc.id);
      });
      marker.on('popupclose', () => {
        if (this.activeId() === loc.id) this.activeId.set(null);
      });
      this.markers.set(loc.id, marker);
    }

    if (mapped.length === 1) {
      this.map.setView([mapped[0].latitude, mapped[0].longitude], 15);
      if (!this.showList()) this.markers.get(mapped[0].id)?.openPopup();
    } else {
      this.map.fitBounds(
        mapped.map((loc) => [loc.latitude, loc.longitude]),
        { padding: [48, 48], maxZoom: 16 },
      );
    }
  }

  /** Scrolls the list (not the page) just far enough to show the entry. */
  private revealListItem(id: string): void {
    const list = this.listEl()?.nativeElement;
    const item = list?.querySelector<HTMLElement>(`[data-location-id="${CSS.escape(id)}"]`);
    if (!list || !item) return;
    const listBox = list.getBoundingClientRect();
    const itemBox = item.getBoundingClientRect();
    if (itemBox.top < listBox.top) {
      list.scrollBy({ top: itemBox.top - listBox.top, behavior: 'smooth' });
    } else if (itemBox.bottom > listBox.bottom) {
      list.scrollBy({ top: itemBox.bottom - listBox.bottom, behavior: 'smooth' });
    }
  }

  // Marker and popup are built as DOM nodes, so shop names never end up as HTML.
  private markerIcon(loc: MappedLocation): L.DivIcon {
    const container = document.createElement('div');
    container.className = 'marker-container';
    const img = document.createElement('img');
    img.src = loc.logo_image_path
      ? this.imageUrl(loc.logo_image_path)
      : 'assets/icons/icon-384x384.png';
    img.alt = '';
    container.append(img);
    return L.divIcon({
      className: 'custom-map-marker',
      html: container,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20],
    });
  }

  private popupContent(loc: MappedLocation): HTMLElement {
    const content = document.createElement('div');
    const name = document.createElement('b');
    name.textContent = loc.company_name;
    content.append(name);
    for (const line of [loc.street, [loc.zip, loc.city].filter(Boolean).join(' ')]) {
      if (!line) continue;
      content.append(document.createElement('br'), line);
    }
    return content;
  }
}
